import {
  Host,
  Input,
  OnInit,
  Optional,
  SkipSelf,
  ViewChild,
  Component,
  ElementRef,
  forwardRef,
  Output,
  EventEmitter,
} from '@angular/core';
import { ControlContainer, NG_VALUE_ACCESSOR } from '@angular/forms';

import { CurrencyTypesEnum } from '@spa/api';

import { AmountControl, BaseControlComponent, Helper } from '../../classes';
import { LanguageService } from '../../language.service';
import {Language} from "../../language";

@Component({
  selector: 'app-amount-input',
  templateUrl: './amount-input.component.html',
  styleUrls: ['./amount-input.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AmountInputComponent),
    multi: true,
  }],
})
export class AmountInputComponent extends BaseControlComponent<any, string, AmountControl> implements OnInit {
  private language: string;
  public currencyPlaceholder: string;

  @ViewChild('input', { static: true }) inputReference: ElementRef;
  @Input() public disabled: boolean;
  @Input() public onlyIntegers: boolean;
  @Input() public currency: string;
  @Input() public allowNegative: boolean;
  @Input() public toFourthDecimal: boolean;
  @Input() public disableMoveCaretToEnd: boolean;
  @Output() public valueChanged: EventEmitter<string | null>;

  constructor(
    @Optional() @Host() @SkipSelf()
    protected controlContainer: ControlContainer,
    protected languageService: LanguageService,
  ) {
    super(controlContainer);

    this.disabled = false;
    this.currencyPlaceholder = 'Currency';
    this.inputReference = {} as ElementRef;
    this.valueChanged = new EventEmitter();
    this.language = languageService.getCurrentLanguage();

    super.registerOnChange((amount: string) => {
      this.valueChanged.emit(amount);
    });
  }

  private formatAmount(value: string, key = ''): string {
	  const decimalSeparator = Helper.getDecimalSeparator(this.language);

    	const valueParts = value.split(decimalSeparator);

	  if (this.onlyIntegers) {
		  return valueParts[0]
	  }

		if (this.language === Language.Bulgarian) {
			valueParts[0] = valueParts[0]
				.replace(/ /g, '')
				.replace(/\./g, '')
				.replace(...Helper.getFormatAmountRegex(this.language));
		}
		else {
			valueParts[0] = valueParts[0]
				.replace(/,/g, '')
				.replace(...Helper.getFormatAmountRegex(this.language));

		}

    	value = valueParts[0];

	  if (!valueParts[1]) {
		  if (key === decimalSeparator) {
			  value += key;
		  }
	  } else {
		  value += decimalSeparator + valueParts[1];
	  }

	  return value;
  }

  ngOnInit(): void {
    this.asFormsControl();
    this.checkInvalid();
  }

  moveCaretToEnd(event: Event): void {
    if (this.disableMoveCaretToEnd) {
      return;
    }

    const target = (event.target as HTMLInputElement);

    if (typeof target.selectionStart === 'number') {
      target.selectionStart = target.selectionEnd = target.value.length;
    }
  }

  convertToFloat(event: Event): void {
    const target = event.target as HTMLInputElement;
	  const separator = Helper.getDecimalSeparator(this.language);

    if (target.value.trim() !== '') {
      const valueParts = target.value.split(separator);

      if (this.onlyIntegers) {
        this.changeValue(valueParts[0]);

        return;
      }

      const secondPartLength = !valueParts[1] ? 0 : valueParts[1].length;

      let value = '';

      if (!this.toFourthDecimal) {
        switch (secondPartLength) {
          case 0:
            value = `${valueParts[0]}${separator}00`;
            break;
          case 1:
            value = `${valueParts[0]}${separator}${valueParts[1]}0`;
            break;
          default:
            value = target.value;
        }
      } else {
        switch (secondPartLength) {
          case 0:
            value = `${valueParts[0]}${separator}0000`;
            break;
          case 1:
            value = `${valueParts[0]}${separator}${valueParts[1]}000`;
            break;
          case 2:
            value = `${valueParts[0]}${separator}${valueParts[1]}00`;
            break;
          case 3:
            value = `${valueParts[0]}${separator}${valueParts[1]}0`;
            break;
          default:
            value = target.value;
        }
      }

      this.changeValue(value);
    } else {
      this.changeValue(null);
    }
  }

  onKeyUp(event: KeyboardEvent): boolean {
    if (event.key === 'Backspace') {
      return this.onKeyPress(event);
    }

    return false;
  }

  onKeyPress(event: KeyboardEvent): boolean {
    const target = event.target as HTMLInputElement;
    const selectionStart = target.selectionStart;
    const selectionEnd = target.selectionEnd;

    if ((selectionStart !== selectionEnd) && (target.value.length === selectionEnd)) {
      target.value = '';
    }

    const minus = '-';
	const separator = Helper.getDecimalSeparator(this.language);
    const regex = Helper.getMatchAmountRegex(true, this.language);
    let key = event.key;
    let isNegative = false;
    let value = target.value;

    if (this.allowNegative) {
      if (key === minus) {
        isNegative = true;
        key = '';
        value = value.replace(minus, '');
      }
      else if (value.startsWith(minus)) {
        isNegative = true;
        value = value.replace(minus, '');
      }
    }

    value = key === 'Backspace' ? value : value + key;

    const pointIndex = value.indexOf(separator);
    const pointValuePosition = this.toFourthDecimal ? value.length - 5 : value.length - 3;
    const pointIndexCheck = pointIndex >= 0 && (pointIndex < pointValuePosition);

    if (!value && this.onChange) {
      this.onChange(null);
    }

    if ((!isNegative && !regex.test(value)) || pointIndexCheck) {
      event.preventDefault();

      return false;
    }
    else {
      value = this.formatAmount(value, key);
    }

    if (isNegative) {
      value = minus + value;
    }

    if (this.onChange) {
      this.onChange(value);
    }

    this.checkInvalid();
    this.control?.resetErrors();

    super.writeValue(value);

    target.value = value;

    event.preventDefault();

    return true;
  }

  onPaste(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData;
    const regex = Helper.getMatchAmountRegex(this.toFourthDecimal, this.language);
    const minus = '-';

    let value = clipboardData ? clipboardData.getData('text') : '';

    setTimeout(() => {
      let isNegative = false;

      if (this.allowNegative && value.startsWith(minus)) {
        isNegative = true;
        value = value.replace(minus, '');
      }

      if (!regex.test(value)) {
        this.changeValue('');
      } else {
        if (isNegative) {
          value = minus + value;
        }
        value = this.formatAmount(value);
        this.changeValue(value);
      }
    }, 0);
  }

  changeValue(value: string | null): void {
    if (this.onChange) {
      this.onChange(value);
    }

    this.writeValue(value);
  }

  writeValue(value: string | null): void {
    this.inputReference.nativeElement.value = value;
    this.checkInvalid();
    this.control?.resetErrors();

    super.writeValue(value);
  }

  asFormsControl(): AmountControl {
    if (!this.control) {
      this.control = new AmountControl({
        name: '',
        value: null,
        placeholder: '',
        validators: [],
        componentType: 0,
        invalid: false,
        errors: [],
        currency: CurrencyTypesEnum.Usd,
      });

	  if (this.currency) {
		  (this.control as AmountControl).currency = this.currency;
	  }
    }

    return this.control as AmountControl;
  }
}
